// Kind views (hq Decision web-kind-views): how this reference client shows one kind, as examples of a frontend over
// the API. Each is one file, src/views/<kind>.tsx (hq.infra.Deployment.tsx), found at build time by tools/views.ts
// (the virtual module "hq:views", one lazy chunk each). The shell knows only this registry: a kind without a view
// gets the generic record (components/fields.tsx). Nothing kind-specific lives in the ontology, the features or the shell.
import { type ComponentType, useEffect, useState } from "react";
import views from "hq:views";
import { api, type Detail } from "@/api";

/** What a view file exports; every part optional. */
export type KindView = {
	/** The record's short name (titles, links to it); null: the generic one. */
	name?: (d: Detail) => string | null;
	/** The record's body in place of the generic fields (it may show them too: <Fields skip={…}/>). */
	Body?: ComponentType<{ detail: Detail; onOpen: (id: string) => void }>;
	/** Links in any record's head (a thread's included): where it was read from, what it belongs to. */
	Head?: ComponentType<{ detail: Detail; onOpen: (id: string) => void }>;
	/** Settings › Instances: a section below the instances (`open`: the instance shown after it). */
	Instances?: ComponentType<{ open: string | null; onOpen: (instance: string) => void }>;
	/** Settings › Instances › <name>: rows on the instance's page. */
	Instance?: ComponentType<{ name: string }>;
	/** Pages at their own path (an OAuth redirect: "/connect/cloudflare"). */
	pages?: Record<string, ComponentType>;
	/** Entries in the nav's views, after the shell's own: one of its pages, its label (read at render), a 16-unit
	 *  stroke path for its icon. */
	nav?: { path: string; label: () => string; icon: string }[];
};

const loaded = new Map<string, Promise<KindView>>();
const load = (kind: string) => {
	let p = loaded.get(kind);
	if (!p) loaded.set(kind, (p = views[kind]!() as Promise<KindView>));
	return p;
};

/** The kind's view: undefined while its chunk loads, null when it has none. */
export function useView(kind: string | null | undefined): KindView | null | undefined {
	const has = !!kind && kind in views;
	const [v, setV] = useState<{ kind: string; view: KindView } | null>(null);
	useEffect(() => {
		if (has) void load(kind).then((view) => setV({ kind, view }));
	}, [kind, has]);
	if (!has) return null;
	return v?.kind === kind ? v.view : undefined;
}

/** Every view (their slots and pages): [] until all are loaded. */
export function useViews(): KindView[] {
	const [all, setAll] = useState<KindView[]>([]);
	useEffect(() => {
		void Promise.all(Object.keys(views).map(load)).then(setAll);
	}, []);
	return all;
}

/** A record's short name: its view's, else its label, else its summary's head ("cloudflare: Workers…" → cloudflare). */
export function nameOf(d: Detail, view?: KindView | null): string {
	const own = view?.name?.(d);
	if (own) return own;
	if (d.name !== d.id) return d.name;
	const summary = d.fields.find((f) => f.key === "summary")?.value;
	const head = summary?.split(/[:.]/)[0]?.trim();
	return head && head.length <= 32 ? head : d.id;
}

const names = new Map<string, Promise<string>>();
/** A referenced record's name (links to it): read once per id. */
export function loadName(id: string): Promise<string> {
	let p = names.get(id);
	if (!p)
		names.set(
			id,
			(p = api.entities.get(id).then(
				async (d) => nameOf(d, d.fullKind && d.fullKind in views ? await load(d.fullKind) : null),
				() => id,
			)),
		);
	return p;
}

/** A field's value parsed as JSON (observed, limits, settings); null when absent or not JSON. */
export function json<T = unknown>(d: Detail, key: string): T | null {
	const v = d.fields.find((f) => f.key === key)?.value;
	try {
		return v === undefined ? null : (JSON.parse(v) as T);
	} catch {
		return null;
	}
}

/** A field's plain value. */
export const field = (d: Detail, key: string) => d.fields.find((f) => f.key === key);
